import { RecursiveFormNode } from '../recursiveFormConfig';
import type { AnswerReviewEvidence } from './AnswerReviewWorkbench.types';
import { FormSubmissionAdapterError } from './formSubmissionFields';
import type { FormAnswerNode, FormAnswerTarget, FormAnswerTree, FormAnswerValueInput, FormNodeAssessment } from './recursiveAnswerContract';
interface NodeBase {
  schema: RecursiveFormNode;
  target: FormAnswerTarget;
  isNull: boolean;
  assessment?: FormNodeAssessment;
  references: AnswerReviewEvidence[];
}
export type RecursiveAnswerNode = NodeBase & ({
  kind: 'simple';
  value: string | string[] | null;
} | {
  kind: 'object';
  properties: RecursiveAnswerNode[];
} | {
  kind: 'array';
  items: {
    id: string | null;
    key: string;
    node: RecursiveAnswerNode;
  }[];
});
export function answerTargetKey(target: FormAnswerTarget): string {
  return JSON.stringify([target.schemaNodeID, target.arrayItemID]);
}

// Draft keys identify mounted items until the server allocates their UUIDs.
const DRAFT_ITEM_PREFIX = 'draft:';
export function createDraftItemKey(): string {
  return `${DRAFT_ITEM_PREFIX}${crypto.randomUUID()}`;
}
export function isDraftItemKey(key: string | null | undefined): boolean {
  return key?.startsWith(DRAFT_ITEM_PREFIX) ?? false;
}
const kinds: Record<RecursiveFormNode['fieldType'], FormAnswerNode['kind']> = {
  shortText: 'SHORT_TEXT',
  longText: 'LONG_TEXT',
  number: 'NUMBER',
  date: 'DATE',
  singleSelect: 'SINGLE_SELECT',
  multiSelect: 'MULTI_SELECT',
  object: 'OBJECT',
  array: 'ARRAY'
};
const valueFields = ['textValue', 'numberValue', 'dateValue', 'singleSelectValue', 'multiSelectValue', 'itemIDs'] as const;
function hasContent(node: RecursiveAnswerNode): boolean {
  if (node.isNull) {
    return false;
  }
  if (node.kind === 'object') {
    return node.properties.some(hasContent);
  }
  return true;
}
export function decimalError(value: string): string | undefined {
  if (!/^-?(0|[1-9]\d*)(\.\d+)?$/.test(value) || value.replace(/[-.]/g, '').length > 64 || (value.split('.')[1]?.length ?? 0) > 18) {
    return 'Enter a decimal with at most 64 digits and 18 decimal places, without an exponent.';
  }
  return undefined;
}
export function simpleValueError(schema: RecursiveFormNode, value: string | string[] | null): string | undefined {
  if (value === null) {
    return undefined;
  }
  if (schema.fieldType === 'object' || schema.fieldType === 'array') {
    return 'Expected a simple field.';
  }
  if (schema.fieldType === 'multiSelect') {
    if (!Array.isArray(value) || new Set(value).size !== value.length || value.some(item => !schema.options?.some(option => option.value === item))) {
      return 'Select valid, distinct options.';
    }
    return undefined;
  }
  if (typeof value !== 'string') {
    return 'Expected a text value.';
  }
  if (schema.fieldType === 'number') {
    return decimalError(value);
  }
  if (schema.fieldType === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return 'Enter a date as YYYY-MM-DD.';
    }
    const [year, month, day] = value.split('-').map(Number);
    // Date arithmetic validates the calendar only; the original string is the wire value.
    const date = new Date(`${value}T00:00:00Z`);
    if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) {
      return 'Enter a valid calendar date.';
    }
  }
  if (schema.fieldType === 'singleSelect' && !schema.options?.some(option => option.value === value)) {
    return 'Select a valid option.';
  }
  if (schema.validation?.maxLength !== undefined && value.length > schema.validation.maxLength) {
    return `Use at most ${schema.validation.maxLength} characters.`;
  }
  return undefined;
}

/** Reconstruct only configured occurrences. Extra, duplicate, or unreachable targets fail closed. */
export function readAnswerTree(schema: RecursiveFormNode, tree: FormAnswerTree, references: ReadonlyMap<string, AnswerReviewEvidence[]> = new Map()): RecursiveAnswerNode {
  const fail = (): never => {
    throw new FormSubmissionAdapterError('Invalid recursive answer data.');
  };
  const nodes = new Map(tree.nodes.map(node => [answerTargetKey(node.target), node]));
  const assessments = new Map(tree.assessments.map(assessment => [answerTargetKey(assessment.target), assessment]));
  if (nodes.size !== tree.nodes.length || assessments.size !== tree.assessments.length) {
    fail();
  }
  const visited = new Set<string>();
  const itemIDs = new Set<string>();
  const read = (definition: RecursiveFormNode, arrayItemID: string | null): RecursiveAnswerNode => {
    const target = {
      schemaNodeID: definition.id,
      arrayItemID
    };
    const key = answerTargetKey(target);
    const record = nodes.get(key);
    if (!record || record.kind !== kinds[definition.fieldType] || visited.has(key)) {
      return fail();
    }
    visited.add(key);
    const assessment = assessments.get(key);
    const evidence = references.get(key) ?? [];
    const base: NodeBase = {
      schema: definition,
      target,
      isNull: record.isNull,
      assessment,
      references: evidence
    };
    const populatedFields = valueFields.filter(field => record[field] != null);
    let result: RecursiveAnswerNode;
    if (record.isNull) {
      if (populatedFields.length) {
        fail();
      }
      if (definition.fieldType === 'object') {
        result = {
          ...base,
          kind: 'object',
          properties: []
        };
      } else if (definition.fieldType === 'array') {
        result = {
          ...base,
          kind: 'array',
          items: []
        };
      } else {
        result = {
          ...base,
          kind: 'simple',
          value: null
        };
      }
    } else if (definition.fieldType === 'object') {
      if (populatedFields.length) {
        fail();
      }
      result = {
        ...base,
        kind: 'object',
        properties: definition.properties.map(property => read(property, arrayItemID))
      };
    } else if (definition.fieldType === 'array') {
      if (populatedFields.length !== 1 || populatedFields[0] !== 'itemIDs' || !record.itemIDs) {
        return fail();
      }
      result = {
        ...base,
        kind: 'array',
        items: record.itemIDs.map(id => {
          if (itemIDs.has(id)) {
            fail();
          }
          itemIDs.add(id);
          const child = read(definition.items, id);
          return {
            id,
            key: id,
            node: child
          };
        })
      };
    } else {
      let field: (typeof valueFields)[number];
      switch (definition.fieldType) {
        case 'shortText':
        case 'longText':
          field = 'textValue';
          break;
        case 'number':
          field = 'numberValue';
          break;
        case 'date':
          field = 'dateValue';
          break;
        case 'singleSelect':
          field = 'singleSelectValue';
          break;
        case 'multiSelect':
          field = 'multiSelectValue';
          break;
      }
      const value = record[field];
      if (populatedFields.length !== 1 || populatedFields[0] !== field || value == null || (field === 'multiSelectValue' ? !Array.isArray(value) || value.some(item => typeof item !== 'string') : typeof value !== 'string')) {
        return fail();
      }
      result = {
        ...base,
        kind: 'simple',
        value
      };
    }
    return result;
  };
  const root = read(schema, null);
  if (visited.size !== nodes.size || [...assessments.keys(), ...references.keys()].some(key => !visited.has(key))) {
    fail();
  }
  return root;
}
export function emptyAnswerNode(schema: RecursiveFormNode, arrayItemID: string | null = null): RecursiveAnswerNode {
  const base = {
    schema,
    target: {
      schemaNodeID: schema.id,
      arrayItemID
    },
    isNull: true,
    references: []
  };
  if (schema.fieldType === 'object') {
    return {
      ...base,
      kind: 'object',
      properties: []
    };
  }
  if (schema.fieldType === 'array') {
    return {
      ...base,
      kind: 'array',
      items: []
    };
  }
  return {
    ...base,
    kind: 'simple',
    value: null
  };
}
export function answerNodeChildren(node: RecursiveAnswerNode): RecursiveAnswerNode[] {
  if (node.kind === 'object') {
    return node.properties;
  }
  if (node.kind === 'array') {
    return node.items.map(item => item.node);
  }
  return [];
}
export function flattenAnswerNodes(node: RecursiveAnswerNode): RecursiveAnswerNode[] {
  return [node, ...answerNodeChildren(node).flatMap(flattenAnswerNodes)];
}
export function serializeAnswerNode(node: RecursiveAnswerNode): FormAnswerValueInput {
  if (node.isNull) {
    return {
      isNull: true
    };
  }
  if (node.kind === 'object' && !hasContent(node)) {
    throw new FormSubmissionAdapterError('Enter at least one property before saving this object.');
  }
  if (node.kind === 'array' && node.items.some(item => item.node.kind === 'object' && item.node.isNull)) {
    throw new FormSubmissionAdapterError('Enter at least one property in each object item, or delete the item.');
  }
  if (node.kind === 'object') {
    return {
      isNull: false,
      properties: node.properties.map(property => ({
        schemaNodeID: property.schema.id,
        value: serializeAnswerNode(property)
      }))
    };
  }
  if (node.kind === 'array') {
    return {
      isNull: false,
      items: node.items.map(item => ({
        id: item.id,
        value: serializeAnswerNode(item.node)
      }))
    };
  }
  const value = node.value;
  const error = simpleValueError(node.schema, value);
  if (error || value === null) {
    throw new FormSubmissionAdapterError(error ?? 'A populated node must have a value.');
  }
  const base = {
    isNull: false
  };
  switch (node.schema.fieldType) {
    case 'number':
      return {
        ...base,
        numberValue: String(value)
      };
    case 'date':
      return {
        ...base,
        dateValue: String(value)
      };
    case 'singleSelect':
      return {
        ...base,
        singleSelectValue: String(value)
      };
    case 'multiSelect':
      return {
        ...base,
        multiSelectValue: node.schema.options?.filter(option => value.includes(option.value)).map(option => option.value) ?? []
      };
    case 'shortText':
    case 'longText':
      return {
        ...base,
        textValue: String(value)
      };
    default:
      throw new FormSubmissionAdapterError('Expected a simple schema.');
  }
}

/** Use the wire validator for both inline feedback and the autosave gate. */
export function recursiveAnswerValidationMessage(node: RecursiveAnswerNode): string | undefined {
  try {
    serializeAnswerNode(node);
    return undefined;
  } catch (error) {
    if (!(error instanceof FormSubmissionAdapterError)) {
      throw error;
    }
    return error.message;
  }
}